import { useRowDrag } from '@/components/admin/npc/use-row-drag';
import { IconButton } from '@/components/admin/reglages/reference-list';
import { RequiredMark } from '@/components/admin/required-mark';
import {
  type NpcDraft,
  type NpcDraftSource,
  canAddSource,
  draftSourcesChars,
  newDraftSource,
  sourceTextRoom,
  withSource,
  withSourceAt,
  withSourceMoved,
} from '@/lib/npc-draft';
import { counterLabel, wordCount } from '@/lib/npc-labels';
import {
  NPC_SOURCES_TEXT_MAX,
  NPC_SOURCE_PROVENANCE_MAX,
  NPC_SOURCE_TITLE_MAX,
  NPC_SOURCE_URL_MAX,
} from '@matter_extractor/shared';
import { Icon, TextArea, TextField, Tooltip } from '@pms-digital/design-system';
import { useId, useState } from 'react';

/**
 * « Ses sources » of the NPC's fiche (npc.md §3.2, H7; maquette `Fiche`, planche 1).
 *
 * An ORDERED list of cards, each holding a title, an optional address, the pasted text and a note of
 * provenance. Four things worth knowing:
 *
 *   - **The address is never read by the server** and the text is never attached as a file (H7):
 *     both are plain fields, and the line under the heading says so.
 *   - **The text is FOLDED** by default on a source that has one: a text of eight hundred words is a
 *     wall, and what an operator checks on opening is the title and the provenance. « Déplier le
 *     texte » turns the preview into the textarea; a new, empty source opens unfolded.
 *   - **The bound is held by the field.** The textarea's `maxLength` is the room the other sources
 *     leave under the 100 000 characters of H7, so no pasted text can push the total past it and no
 *     refusal can come back for it; a paste that does not fit is cut by the browser, and the counter
 *     says how full the whole is.
 *   - **Two ways to move a source**, the grip's native drag and the arrows that name what they do.
 *
 * Read only (« Voir », a retired NPC): every control is `readOnly` and the buttons that write are
 * absent, never `disabled`, so the text stays selectable.
 */

export interface NpcSourcesEditorProps {
  draft: NpcDraft;
  readOnly: boolean;
  onDraft: (next: NpcDraft) => void;
}

export function NpcSourcesEditor({ draft, readOnly, onDraft }: NpcSourcesEditorProps) {
  const headingId = useId();
  const drag = useRowDrag(
    draft.sources.map((source) => source.key),
    (key, index) => onDraft(withSourceAt(draft, key, index)),
  );
  const total = draftSourcesChars(draft);

  return (
    <section aria-labelledby={headingId} data-testid="npc-sources" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <h3 id={headingId} className="font-semibold text-gray-800 text-small">
          Ses sources
          <RequiredMark />{' '}
          <span className="font-normal text-gray-600">({draft.sources.length})</span>
        </h3>
        <p data-testid="npc-sources-compteur" className="text-caption text-gray-500 leading-4">
          {counterLabel(total, NPC_SOURCES_TEXT_MAX)}
        </p>
      </div>
      <p className="text-caption text-gray-600 leading-4">
        Toutes servies ensemble au joueur, dans cet ordre. Le texte se colle tel quel ; l’adresse
        n’est jamais lue par le serveur.
      </p>

      <ol className="space-y-3">
        {draft.sources.map((source, index) => {
          const hint = drag.hint?.key === source.key ? drag.hint : null;
          return (
            <li
              key={source.key}
              data-testid="npc-source"
              {...drag.row(source.key)}
              className={`flex items-start gap-2 border-2 ${
                hint === null
                  ? 'border-transparent'
                  : `border-bleu-nuit ${hint.after ? 'border-b-4' : 'border-t-4'}`
              } ${drag.dragging === source.key ? 'opacity-40' : ''}`}
            >
              {readOnly ? null : (
                <div className="flex shrink-0 flex-col items-center gap-1 pt-2">
                  <Tooltip content="Glisser pour réordonner">
                    <span
                      {...drag.grip(source.key)}
                      data-testid="npc-source-poignee"
                      className="flex h-8 w-8 cursor-grab items-center justify-center rounded-md bg-gray-100 text-gray-600"
                    >
                      <span className="inline-flex text-gray-400">
                        <Icon name="drag_indicator" size={16} />
                      </span>
                      <span className="sr-only">Source {index + 1}</span>
                    </span>
                  </Tooltip>
                  <IconButton
                    label={`Monter la source ${index + 1}`}
                    disabled={index === 0}
                    testId="npc-source-monter"
                    onClick={() => onDraft(withSourceMoved(draft, source.key, -1))}
                  >
                    <Icon name="arrow_upward" size={16} />
                  </IconButton>
                  <IconButton
                    label={`Descendre la source ${index + 1}`}
                    disabled={index === draft.sources.length - 1}
                    testId="npc-source-descendre"
                    onClick={() => onDraft(withSourceMoved(draft, source.key, 1))}
                  >
                    <Icon name="arrow_downward" size={16} />
                  </IconButton>
                </div>
              )}
              <SourceCard
                source={source}
                index={index}
                readOnly={readOnly}
                room={sourceTextRoom(draft, source.key)}
                onChange={(patch) => onDraft(withSource(draft, source.key, patch))}
                onRemove={() =>
                  onDraft({
                    ...draft,
                    sources: draft.sources.filter((one) => one.key !== source.key),
                  })
                }
              />
            </li>
          );
        })}
      </ol>

      {draft.sources.length === 0 ? (
        <p data-testid="npc-sources-vide" className="text-gray-600 text-small italic">
          Aucune source : un NPC en porte au moins une, sinon le joueur n’a rien à jouer.
        </p>
      ) : null}

      {readOnly ? null : (
        <div className="flex items-start gap-2">
          <button
            type="button"
            data-testid="npc-source-ajouter"
            disabled={!canAddSource(draft)}
            onClick={() => onDraft({ ...draft, sources: [...draft.sources, newDraftSource()] })}
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border-2 border-gray-300 border-dashed bg-white px-4 font-semibold text-bleu-nuit text-small hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-500"
          >
            <Icon name="add" size={16} />
            Ajouter une source
          </button>
        </div>
      )}
    </section>
  );
}

interface SourceCardProps {
  source: NpcDraftSource;
  index: number;
  readOnly: boolean;
  /** How many characters the text may still hold (H7). */
  room: number;
  onChange: (patch: Partial<Omit<NpcDraftSource, 'key'>>) => void;
  onRemove: () => void;
}

function SourceCard({ source, index, readOnly, room, onChange, onRemove }: SourceCardProps) {
  // A source with a text opens FOLDED; a new one, empty, is what somebody is about to paste into.
  const [unfolded, setUnfolded] = useState(source.text === '');
  const words = wordCount(source.text);

  return (
    <div className="min-w-0 flex-1 space-y-3 rounded-md border-2 border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem] flex-[2]">
          {/* The asterisk rides in the label: the field is `aria-required` and not
              `required`, the régime it had before the package field. */}
          <TextField
            label={
              <>
                Titre
                <RequiredMark />
              </>
            }
            data-testid="npc-source-titre"
            value={source.title}
            readOnly={readOnly}
            maxLength={NPC_SOURCE_TITLE_MAX}
            aria-required="true"
            onChange={(event) => onChange({ title: event.currentTarget.value })}
          />
        </div>
        <div className="min-w-[12rem] flex-[3]">
          <TextField
            label={
              <>
                Adresse <span className="text-gray-600">(facultatif)</span>
              </>
            }
            data-testid="npc-source-adresse"
            value={source.url}
            readOnly={readOnly}
            maxLength={NPC_SOURCE_URL_MAX}
            onChange={(event) => onChange({ url: event.currentTarget.value })}
          />
        </div>
        {readOnly ? null : (
          <IconButton
            label={`Retirer la source ${index + 1}`}
            tone="danger"
            testId="npc-source-retirer"
            onClick={onRemove}
          >
            <Icon name="delete" size={16} />
          </IconButton>
        )}
      </div>

      <div className="space-y-1">
        {/* The visible heading of the text, with the fold beside it: unfolded, the
            package field carries the same words as its (hidden) label, so the heading
            is hidden from assistive technology rather than read twice. */}
        <div className="flex items-baseline justify-between gap-3">
          <p
            aria-hidden={unfolded ? true : undefined}
            className="block font-semibold text-caption text-gray-700"
          >
            Texte <span className="font-normal text-gray-600">· {words} mots</span>
          </p>
          {source.text === '' ? null : (
            <button
              type="button"
              data-testid="npc-source-plier"
              aria-expanded={unfolded}
              onClick={() => setUnfolded((was) => !was)}
              className="cursor-pointer text-bleu-nuit text-caption underline underline-offset-2 hover:text-bleu-nuit-deep"
            >
              {unfolded ? 'Replier le texte' : 'Déplier le texte'}
            </button>
          )}
        </div>
        {unfolded ? (
          <TextArea
            label={`Texte · ${words} mots`}
            hideLabel
            data-testid="npc-source-texte"
            value={source.text}
            readOnly={readOnly}
            maxLength={Math.max(room, source.text.length)}
            rows={8}
            aria-required="true"
            onChange={(event) => onChange({ text: event.currentTarget.value })}
          />
        ) : (
          <p
            data-testid="npc-source-texte-replie"
            className="line-clamp-3 whitespace-pre-line rounded-md border border-gray-200 bg-white px-3 py-2 text-gray-800 text-small"
          >
            {source.text}
          </p>
        )}
      </div>

      <TextField
        label={
          <>
            Note de provenance <span className="text-gray-600">(facultatif)</span>
          </>
        }
        data-testid="npc-source-provenance"
        value={source.provenance}
        readOnly={readOnly}
        maxLength={NPC_SOURCE_PROVENANCE_MAX}
        placeholder="Traduction, édition, ce qui a été réparé"
        onChange={(event) => onChange({ provenance: event.currentTarget.value })}
      />
    </div>
  );
}
